import type { CSSProperties, ComponentType, ReactNode } from "react";
import { pathFor, type RouteId } from "../../app/router/routes";
import type { GameId } from "../../shared/curriculum";
import { useCopy, useLang } from "../../shared/i18n";
import { MARKETING_COPY, type MarketingCopy, type SkillKey } from "./copy";
import { GAME_PAGE_IDS, GAME_PAGE_META, marketingGame, type MarketingGame, type MoveIcon } from "./games";
import { heroBackdrop, useDocumentSeo, useGamePageContent, type GamePageContent } from "./siteContent";
import { DraftBar } from "./sections/DraftBar";
import {
  ArrowLeftIcon,
  ChildrenIcon,
  ClockIcon,
  CountIcon,
  DropIcon,
  DuckIcon,
  EarIcon,
  EyeIcon,
  FootprintsIcon,
  GrabIcon,
  HandIcon,
  InfoIcon,
  JumpIcon,
  CameraIcon,
  LayersIcon,
  LeanIcon,
  LockIcon,
  PlayIcon,
  PointIcon,
  PoseIcon,
  RunIcon,
  TargetIcon,
  TickIcon,
  type IconProps,
} from "./icons";
import { Reveal } from "./Reveal";
import { requestSection, useSectionSpy } from "./useSectionSpy";
import { BackdropVideo } from "./sections/BackdropVideo";
import { CtaLink } from "./sections/Cta";
import { FinalCta } from "./sections/FinalCta";
import { GameCard, SkillChip } from "./sections/GamesShowcase";
import { Section, SectionHeading } from "./sections/primitives";
import { SiteFooter } from "./sections/SiteFooter";
import { SiteHeader } from "./sections/SiteHeader";

/** One icon per movement a page names. */
const MOVE_ICON: Record<MoveIcon, ComponentType<IconProps>> = {
  count: CountIcon,
  lean: LeanIcon,
  hold: HandIcon,
  jump: JumpIcon,
  duck: DuckIcon,
  listen: EarIcon,
  step: FootprintsIcon,
  centre: TargetIcon,
  watch: EyeIcon,
  pose: PoseIcon,
  wait: ClockIcon,
  run: RunIcon,
  point: PointIcon,
  grab: GrabIcon,
  drop: DropIcon,
};

/** Each skill's token, for the places a colour is a value (bars, gradients) rather than a class. */
const SKILL_VAR: Record<SkillKey, string> = {
  numeracy: "var(--color-skill-numeracy)",
  literacy: "var(--color-skill-literacy)",
  generalAwareness: "var(--color-skill-general-awareness)",
  socialAwareness: "var(--color-skill-social-awareness)",
  patterns: "var(--color-skill-matching)",
  coordination: "var(--color-skill-coordination)",
  balance: "var(--color-skill-balance)",
  bodyAwareness: "var(--color-skill-body-awareness)",
};

/**
 * The light behind each page's "moves" band.
 *
 * 🔴 NOT THE SKILL COLOUR. Sunshine and literacy pink, mixed into the band's
 * deep indigo, go muddy — olive and plum — so each band glows in the colour of
 * its game's *world* (Sky Quest's sky, Letter Lagoon's water) and the skill
 * colour stays on the chips, where it reads clean.
 */
const BAND_GLOW: Record<GameId, string> = {
  "sky-quest": "var(--color-sky)",
  "turos-world": "var(--color-sky)",
  "letter-lagoon": "var(--color-teal)",
  "bridge-patterns": "color-mix(in srgb, var(--color-indigo) 60%, var(--color-sky))",
  "kalqy-world": "var(--color-grape)",
};

const HOW_TO_PLAY_ID = "how-to-play";

export interface GamePageProps {
  gameId: GameId;
  /** "Log in" — opens the door picker (`#/start`). */
  onSignIn?: () => void;
}

/**
 * A game's own page — `#/sky-quest`, `#/turos-world`, `#/letter-lagoon`,
 * `#/bridge-patterns`.
 *
 * 🔴 EVERYTHING A SCHOOL WOULD ASK ABOUT ONE GAME, AND NOTHING THE GAME DOES NOT
 * DO. The levels — names, objectives, NCF-FS codes — are read off the
 * curriculum registry (`content.levels`), not retyped, so the page cannot
 * advertise a level the product lacks. The steps and the grown-ups' note are the
 * game's own how-to-play text. The teacher report is labelled "Sample data".
 *
 * ⚠️ ONE BUTTON DIFFERS BY GAME, AND IT IS THE HONEST ONE. Sky Quest's page
 * offers "Play free now" — its free round is public. The other three need a
 * school behind them, so their first action is Connect, and the second only
 * scrolls to how the game plays; there is no free round to send anyone to.
 */
export function GamePage({ gameId, onSignIn }: GamePageProps) {
  const t = useCopy(MARKETING_COPY);
  const spy = useSectionSpy();
  const game = marketingGame(gameId);
  /* Built-in copy first, then what an operator published (P3-21) — see `siteContent.ts`. */
  const { content: copy, draft } = useGamePageContent(gameId);
  useDocumentSeo(copy.seo);

  return (
    <div className="dash-text min-h-screen overflow-x-clip bg-surface font-dash text-ink selection:bg-primary-tint selection:text-primary">
      <a
        href={`#${HOW_TO_PLAY_ID}`}
        onClick={(e) => {
          e.preventDefault();
          scrollToHowToPlay();
        }}
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-100 focus:rounded-pill focus:bg-primary focus:px-5 focus:py-3 focus:font-dash focus:text-dash-sm focus:font-bold focus:text-primary-ink"
      >
        {t.skipToContent}
      </a>

      <SiteHeader t={t} spy={spy} onSignIn={onSignIn} standalone overlay current={gameId as RouteId} />

      <main>
        <GameHero t={t} game={game} copy={copy} />
        <HowToPlay t={t} game={game} copy={copy} />
        <Levels t={t} game={game} copy={copy} />
        <Builds t={t} game={game} copy={copy} />
        <Teachers t={t} game={game} />
        <MoreGames t={t} gameId={gameId} />
        <FinalCta
          t={t}
          heading={game.free ? t.gamePage.finalFree : t.gamePage.finalFor(game.title)}
        />
      </main>

      <div className="mt-24 lg:mt-32">
        <SiteFooter t={t} />
      </div>
      {draft ? <DraftBar version={draft.version} back={`#/platform/website?tab=games&page=${gameId}`} /> : null}
    </div>
  );
}

function scrollToHowToPlay(): void {
  const el = document.getElementById(HOW_TO_PLAY_ID);
  if (!el) return;
  const reduce = document.documentElement.dataset.reduceMotion === "true";
  el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  el.focus({ preventScroll: true });
}

interface PartProps {
  t: MarketingCopy;
  game: MarketingGame;
  copy: GamePageContent;
}

/* ── Hero ──────────────────────────────────────────────────────────────── */

function GameHero({ t, game, copy }: PartProps) {
  const meta = GAME_PAGE_META[game.id];
  const FirstMove = MOVE_ICON[meta.moveIcons[0]];

  return (
    <section className="relative isolate overflow-hidden bg-sky">
      <BackdropVideo
        backdrop={heroBackdrop(copy.heroMedia)}
        pauseLabel={t.video.pause}
        playLabel={t.video.play}
        objectPosition="68% center"
        controlClassName="absolute right-4 bottom-20 sm:right-6 lg:top-32 lg:right-10 lg:bottom-auto xl:right-[max(2.5rem,calc((100vw_-_1240px)/2_+_2.5rem))]"
      />
      <div aria-hidden="true" className="kq-hero-shade pointer-events-none absolute inset-0" />

      <div className="kq-on-photo relative z-10 mx-auto w-full max-w-[1240px] px-5 pt-28 pb-28 text-surface kq-hero-frame flex flex-col justify-center sm:px-6 sm:pt-32 lg:px-10 lg:pt-28 lg:pb-32">
        <nav aria-label={t.gamePage.breadcrumb} className="kq-rise-in">
          <ol className="flex items-center gap-2.5 font-dash text-dash-xs font-extrabold">
            <li>
              <a
                href="#/"
                onClick={() => {
                  requestSection("games");
                }}
                className="inline-flex items-center gap-2 rounded-pill border border-surface/55 bg-surface/18 py-1.5 pr-3.5 pl-2.5 outline-none backdrop-blur-md hover:bg-surface/28 focus-visible:ring-2 focus-visible:ring-surface"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                {t.gamePage.back}
              </a>
            </li>
            <li aria-hidden="true" className="text-surface/60">
              /
            </li>
            <li aria-current="page">{game.title}</li>
          </ol>
        </nav>

        <div className="kq-rise-in mt-6 flex flex-wrap items-center gap-2" style={{ "--kq-delay": "80ms" } as CSSProperties}>
          <SkillChip t={t} game={game} />
          {game.free ? (
            <span className="rounded-pill bg-success px-3 py-1 font-dash text-dash-xs font-extrabold text-surface">
              {t.games.freeBadge}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-pill bg-ink/60 px-3 py-1 font-dash text-dash-xs font-extrabold text-surface">
              <LockIcon className="h-3.5 w-3.5" />
              {t.games.lockedBadge}
            </span>
          )}
        </div>

        <h1
          className="kq-rise-in kq-hero mt-3 max-w-[12ch] font-kid font-extrabold"
          style={{ "--kq-delay": "160ms" } as CSSProperties}
        >
          {game.title}
        </h1>
        <p
          className="kq-rise-in mt-5 max-w-[40ch] font-dash text-dash-lg font-bold text-surface/95"
          style={{ "--kq-delay": "240ms" } as CSSProperties}
        >
          {copy.blurb}
        </p>

        <div
          className="kq-rise-in mt-8 flex flex-col gap-4 sm:flex-row sm:items-center"
          style={{ "--kq-delay": "320ms" } as CSSProperties}
        >
          {game.free ? (
            <>
              <CtaLink href={pathFor("sky-quest-play")} variant="sun" icon="play">
                {t.cta.playFreeNow}
              </CtaLink>
              <CtaLink href={pathFor("contact-sales")} variant="glass">
                {t.cta.bookSchoolDemo}
              </CtaLink>
            </>
          ) : (
            <>
              <CtaLink href={pathFor("contact-sales")} variant="primary">
                {t.cta.getForSchool}
              </CtaLink>
              <CtaLink
                href={`#${HOW_TO_PLAY_ID}`}
                variant="glass"
                icon="play"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToHowToPlay();
                }}
              >
                {t.cta.watchInAction}
              </CtaLink>
            </>
          )}
        </div>

        <ul
          className="kq-rise-in mt-8 flex max-w-[44rem] flex-wrap gap-2.5"
          style={{ "--kq-delay": "400ms" } as CSSProperties}
        >
          <Fact icon={<ChildrenIcon className="h-4.5 w-4.5" />}>{t.gamePage.ages}</Fact>
          <Fact icon={<LayersIcon className="h-4.5 w-4.5" />}>{t.gamePage.levels}</Fact>
          <Fact icon={<FirstMove className="h-4.5 w-4.5" />}>{copy.moves.map((m) => m.toLowerCase()).join(" · ")}</Fact>
          <Fact icon={<CameraIcon className="h-4.5 w-4.5" />}>{t.gamePage.camera}</Fact>
        </ul>
      </div>

      <svg
        aria-hidden="true"
        viewBox="0 0 1440 96"
        preserveAspectRatio="none"
        className="absolute inset-x-0 -bottom-px z-10 h-12 w-full text-surface sm:h-20"
      >
        <path fill="currentColor" d="M0 58C180 26 360 18 560 40C760 62 920 86 1120 70C1260 59 1360 40 1440 30V96H0Z" />
      </svg>
    </section>
  );
}

function Fact({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <li className="inline-flex items-center gap-2 rounded-pill border border-surface/45 bg-surface/16 px-3.5 py-2 font-dash text-dash-xs font-extrabold backdrop-blur-md">
      <span aria-hidden="true">{icon}</span>
      {children}
    </li>
  );
}

/* ── How to play ───────────────────────────────────────────────────────── */

function HowToPlay({ t, game, copy }: PartProps) {
  const meta = GAME_PAGE_META[game.id];

  return (
    <Section id={HOW_TO_PLAY_ID} className="pt-20 lg:pt-28">
      <div className="grid items-center gap-12 lg:grid-cols-[1fr_1.05fr] lg:gap-16">
        <Reveal variant="left">
          {/* The game as it looks on the classroom screen — a laptop, drawn in tokens. */}
          <figure>
            <div className="relative rounded-2xl bg-ink p-3 shadow-[0_40px_70px_-30px_color-mix(in_srgb,var(--color-ink)_60%,transparent)] sm:p-4">
              <span aria-hidden="true" className="absolute top-1.5 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-pill bg-muted" />
              <img
                src={game.image}
                alt={t.gamePage.plateAlt(game.title)}
                width={800}
                height={533}
                loading="lazy"
                decoding="async"
                className="aspect-[3/2] w-full rounded-lg object-cover"
              />
              {game.free ? (
                <a
                  href={pathFor("sky-quest-play")}
                  className="absolute top-1/2 left-1/2 inline-flex -translate-x-1/2 -translate-y-1/2 items-center gap-3 rounded-pill bg-surface/95 py-2 pr-6 pl-2 font-dash text-dash-md font-extrabold whitespace-nowrap text-ink shadow-raised outline-none transition-transform duration-150 ease-dash hover:scale-105 focus-visible:ring-2 focus-visible:ring-ink"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-pill bg-primary text-primary-ink shadow-[0_4px_0_var(--color-primary-deep)]">
                    <PlayIcon className="h-5 w-5 translate-x-px" />
                  </span>
                  {t.gamePage.watchRound}
                </a>
              ) : null}
            </div>
            <div aria-hidden="true" className="mx-auto h-3 w-40 rounded-b-lg bg-line-strong" />
          </figure>
        </Reveal>

        <div>
          <SectionHeading label={t.gamePage.howLabel} heading={copy.summary} accentClass={game.dotClass} labelClass="text-ink-soft" />
          <ol className="mt-7 space-y-3">
            {copy.steps.map((step, i) => {
              const Icon = MOVE_ICON[meta.stepIcons[i] ?? "count"];
              return (
                /* By position: the steps are a fixed three, and edited titles may repeat. */
                <li key={i}>
                  <Reveal delay={i * 80} variant="right">
                    <div className="flex items-center gap-4 rounded-xl border border-line bg-surface p-4 sm:gap-5 sm:p-5">
                      <span
                        aria-hidden="true"
                        className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-lg text-surface shadow-[0_4px_0_color-mix(in_srgb,var(--color-ink)_22%,transparent)] ${game.dotClass}`}
                      >
                        <Icon className="h-7 w-7" />
                      </span>
                      <span>
                        <span className="block font-dash text-dash-xs font-bold text-muted">{t.gamePage.stepN(i + 1)}</span>
                        <span className="block font-dash text-dash-lg font-extrabold text-ink">{step.title}</span>
                        <span className="mt-0.5 block font-dash text-dash-sm text-ink-soft">{step.body}</span>
                      </span>
                    </div>
                  </Reveal>
                </li>
              );
            })}
          </ol>
          <Reveal delay={240}>
            <aside className="mt-4 flex gap-3.5 rounded-xl bg-canvas p-5">
              <InfoIcon className="mt-0.5 h-5 w-5 shrink-0 text-ink-soft" />
              <div>
                <h3 className="font-dash text-dash-sm font-extrabold text-ink">{t.gamePage.grownUps}</h3>
                <p className="mt-1 font-dash text-dash-sm text-ink-soft">{copy.adult}</p>
              </div>
            </aside>
          </Reveal>
        </div>
      </div>
    </Section>
  );
}

/* ── Levels ────────────────────────────────────────────────────────────── */

function Levels({ t, game, copy }: PartProps) {
  const { lang } = useLang();

  return (
    <Section className="pt-24 lg:pt-32">
      <SectionHeading
        label={t.gamePage.learnLabel}
        heading={t.gamePage.learnHeading}
        lede={t.gamePage.learnLede}
        accentClass={game.dotClass}
        labelClass="text-ink-soft"
      />
      <ol className="mt-10 grid gap-5 md:grid-cols-3 lg:gap-6">
        {game.content.levels.map((level, i) => (
          <li key={level.level}>
            <Reveal delay={i * 90} className="h-full">
              <article className="flex h-full flex-col overflow-hidden rounded-xl border border-line bg-surface shadow-card">
                <div className={`flex items-center gap-3.5 px-6 py-5 ${game.tintClass}`}>
                  <span
                    aria-hidden="true"
                    className={`flex h-12 w-12 items-center justify-center rounded-pill font-kid text-kid-sm font-extrabold text-surface shadow-[0_4px_0_color-mix(in_srgb,var(--color-ink)_22%,transparent)] ${game.dotClass}`}
                  >
                    {level.level}
                  </span>
                  <span className="font-dash text-dash-sm font-extrabold text-ink">{t.gamePage.levelN(level.level)}</span>
                </div>
                <div className="flex flex-1 flex-col px-6 pt-5 pb-6">
                  <h3 className="font-kid text-kid-sm leading-tight font-extrabold text-ink">{level.name[lang]}</h3>
                  <p className="mt-2 flex-1 font-dash text-dash-sm text-ink-soft">{level.objective[lang]}</p>
                  <ul className="mt-5 flex flex-wrap gap-2">
                    {level.ncf.map((code) => (
                      <li key={code} className="rounded-pill bg-indigo/10 px-3 py-1 font-dash text-dash-xs font-extrabold text-indigo">
                        {t.gamePage.ncf(code)}
                      </li>
                    ))}
                    <li className="rounded-pill border border-line bg-canvas px-3 py-1 font-dash text-dash-xs font-bold text-ink-soft">
                      {copy.levelMoves[i]}
                    </li>
                  </ul>
                </div>
              </article>
            </Reveal>
          </li>
        ))}
      </ol>
    </Section>
  );
}

/* ── The moves build the skills ────────────────────────────────────────── */

function Builds({ t, game, copy }: PartProps) {
  const meta = GAME_PAGE_META[game.id];
  const band = {
    background: `radial-gradient(120% 150% at 100% 0%, ${BAND_GLOW[game.id]} 0%, color-mix(in srgb, var(--color-indigo) 72%, var(--color-ink)) 72%)`,
  };

  return (
    <div className="mx-auto mt-24 w-full max-w-[1240px] px-5 sm:px-6 lg:mt-32 lg:px-10">
      <Reveal>
        <section
          style={band}
          className="grid items-center gap-8 overflow-hidden rounded-2xl px-6 py-10 sm:px-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-12 lg:rounded-[2.75rem] lg:px-14 lg:py-14"
        >
          <div>
            <p className="flex items-center gap-3 font-dash text-dash-xs font-bold text-surface/85">
              <span aria-hidden="true" className="h-0.5 w-7 rounded-pill bg-surface" />
              {t.gamePage.buildsLabel}
            </p>
            <h2 className="kq-h2 mt-3 font-dash font-extrabold tracking-tight text-surface">{t.gamePage.buildsHeading}</h2>
            <ul className="mt-6 flex flex-wrap gap-2">
              {meta.skills.map((skill) => (
                <li
                  key={skill}
                  className="inline-flex items-center gap-2 rounded-pill bg-surface px-3.5 py-1.5 font-dash text-dash-xs font-extrabold text-ink"
                >
                  <span aria-hidden="true" className="h-2.5 w-2.5 rounded-pill" style={{ background: SKILL_VAR[skill] }} />
                  {t.skills[skill]}
                </li>
              ))}
            </ul>
          </div>
          <ul className="grid grid-cols-1 gap-3 min-[420px]:grid-cols-3 sm:gap-4">
            {copy.moves.map((move, i) => {
              const Icon = MOVE_ICON[meta.moveIcons[i] ?? "jump"];
              return (
                <li
                  key={i}
                  className="flex flex-col items-center gap-3 rounded-xl bg-surface p-5 text-center shadow-[0_6px_0_color-mix(in_srgb,var(--color-ink)_18%,transparent)]"
                >
                  <span aria-hidden="true" className={`flex h-16 w-16 items-center justify-center rounded-lg text-ink ${game.tintClass}`}>
                    <Icon className="h-8 w-8" />
                  </span>
                  <span className="font-dash text-dash-sm leading-snug font-extrabold text-ink">{move}</span>
                </li>
              );
            })}
          </ul>
        </section>
      </Reveal>
    </div>
  );
}

/* ── What teachers see ─────────────────────────────────────────────────── */

/** Illustrative — the card says "Sample data" on its face. */
const SAMPLE_DISTRIBUTION = [8, 14, 10] as const;

function Teachers({ t, game }: { t: MarketingCopy; game: MarketingGame }) {
  const r = t.gamePage.report;
  const most = Math.max(...SAMPLE_DISTRIBUTION);
  const colour = SKILL_VAR[game.skill];

  return (
    <Section className="pt-24 lg:pt-32">
      <div className="grid items-center gap-10 lg:grid-cols-[1fr_1fr] lg:gap-16">
        <div>
          <SectionHeading
            label={t.gamePage.teachLabel}
            heading={t.gamePage.teachHeading}
            lede={t.gamePage.teachLede(game.title)}
            accentClass="bg-success"
            labelClass="text-success"
          />
          <ul className="mt-6 space-y-3">
            {t.gamePage.teachPoints.map((point) => (
              <li key={point} className="flex items-center gap-3 font-dash text-dash-sm font-bold text-ink-soft">
                <span
                  aria-hidden="true"
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-pill border border-success/35 bg-success/8 text-success"
                >
                  <TickIcon className="h-4 w-4" />
                </span>
                {point}
              </li>
            ))}
          </ul>
        </div>

        <Reveal variant="zoom">
          <figure className="rounded-2xl border border-line bg-cream p-6 shadow-raised sm:p-7">
            <figcaption className="flex items-start justify-between gap-4">
              <span>
                <span className="block font-kid text-kid-sm leading-none font-extrabold text-ink">{game.title}</span>
                <span className="mt-1 block font-dash text-dash-xs font-bold text-muted">{r.caption}</span>
              </span>
              <span className="rounded-pill border border-line bg-surface px-2.5 py-1 font-dash text-dash-xs font-extrabold text-muted">
                {t.sampleData}
              </span>
            </figcaption>
            <div className="mt-6 space-y-3">
              {SAMPLE_DISTRIBUTION.map((count, i) => (
                <div key={i} className="grid grid-cols-[4.5rem_1fr_2rem] items-center gap-3">
                  <span className="font-dash text-dash-xs font-bold text-ink-soft">{r.levelN(i + 1)}</span>
                  <span className="h-3 overflow-hidden rounded-pill bg-line" role="presentation">
                    <span
                      className="block h-full rounded-pill"
                      style={{ width: `${String(Math.round((count / most) * 100))}%`, background: colour }}
                    />
                  </span>
                  <span className="tabular text-right font-dash text-dash-sm font-extrabold text-ink">{count}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 grid grid-cols-3 gap-2.5">
              <Tile value="6" label={r.readyToMoveUp} className="bg-success/10 text-success" />
              <Tile value="3" label={r.needAHand} className="bg-coral/12 text-danger" />
              <Tile value={t.skills[game.skill]} label={r.mainSkill} className={`${game.tintClass} text-ink`} small />
            </div>
          </figure>
        </Reveal>
      </div>
    </Section>
  );
}

function Tile({ value, label, className, small = false }: { value: string; label: string; className: string; small?: boolean }) {
  return (
    <div className={`rounded-lg p-3 ${className}`}>
      <p className={`font-dash font-extrabold ${small ? "text-dash-sm leading-tight" : "tabular text-dash-xl leading-none"}`}>{value}</p>
      <p className="mt-1 font-dash text-dash-xs font-bold text-ink-soft">{label}</p>
    </div>
  );
}

/* ── More games ────────────────────────────────────────────────────────── */

function MoreGames({ t, gameId }: { t: MarketingCopy; gameId: GameId }) {
  const others = GAME_PAGE_IDS.filter((id) => id !== gameId).map(marketingGame);

  return (
    <div className="mt-24 rounded-t-[2.5rem] bg-canvas pt-20 pb-28 lg:mt-32 lg:rounded-t-[3.5rem] lg:pt-24 lg:pb-32">
      <Section>
        <SectionHeading
          label={t.gamePage.moreLabel}
          heading={t.gamePage.moreHeading}
          accentClass="bg-leaf"
          labelClass="text-success"
          aside={
            <CtaLink
              href="#/"
              variant="outline"
              size="md"
              onClick={() => {
                requestSection("games");
              }}
            >
              {t.cta.allGames}
            </CtaLink>
          }
        />
        <ul className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
          {others.map((game, i) => (
            <li key={game.id}>
              <Reveal delay={i * 80} className="h-full">
                <GameCard t={t} game={game} />
              </Reveal>
            </li>
          ))}
        </ul>
      </Section>
    </div>
  );
}
